import { useEffect, useState, type RefObject } from "react";

/** The bounded wait: a request that never answers (a hung CDN, not a clean 403) would otherwise hold the print-out
 *  forever with no escape hatch (panel). After this, the sheet prints what has loaded; the rest are placeholders. */
export const IMAGES_SETTLE_TIMEOUT_MS = 20_000;

/**
 * True once every `<img>` under `ref` has settled — loaded OR failed — or the bounded wait has elapsed. An image that
 * is already `complete` when the effect attaches counts at once: a cached image fires `load` before any listener can
 * be added, so waiting for its event would never resolve (ADR-031 panel High). The rest settle on `load`/`error`, in
 * parallel. A subtree with no images settles immediately.
 */
export function useImagesSettled(ref: RefObject<HTMLElement | null>, deps: readonly unknown[] = [], timeoutMs = IMAGES_SETTLE_TIMEOUT_MS): boolean {
  const [settled, setSettled] = useState(false);
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const pending = [...root.querySelectorAll("img")].filter((img) => !img.complete);
    if (pending.length === 0) {
      setSettled(true);
      return;
    }
    let left = pending.length;
    let cancelled = false;
    const done = () => {
      left -= 1;
      if (left === 0 && !cancelled) setSettled(true);
    };
    for (const img of pending) {
      img.addEventListener("load", done, { once: true });
      img.addEventListener("error", done, { once: true });
    }
    const deadline = window.setTimeout(() => {
      if (!cancelled) setSettled(true);
    }, timeoutMs);
    return () => {
      cancelled = true;
      window.clearTimeout(deadline);
      for (const img of pending) {
        img.removeEventListener("load", done);
        img.removeEventListener("error", done);
      }
    };
  }, deps);
  return settled;
}
